import { Grow } from '@mui/material';
import { timeDay } from 'd3';
import { format } from 'date-fns';
import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import React, { useContext } from 'react';
import { mdiMessageTextOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { MessagingContext } from '../../../services';
import { AttachmentButton } from '../attachment-button';
import { NoteIndicator } from '../note-indicator';
import { authorNickname, IMessagingComment, isHtmlContent, sanitizeHtml, normalizeMarkdown } from '../util';
import * as S from './chat-history.styles';

interface IChatHistory {
    hideNote?: boolean;
}

export const ChatHistory: React.FunctionComponent<IChatHistory> = (props: IChatHistory): React.ReactElement => {
    const { hideNote } = props;
    const context = useContext(MessagingContext);
    const { comments, documents } = context;

    const mdyFormat = (d: Date): string => format(d, 'MMM d, yyyy');
    const timeFormat = (d: Date): string => format(d, 'h:mm a');
    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());

    const isNewDay = (currDate?: Date, nextDate?: Date): boolean => {
        let isSameDay = false;
        if (currDate && nextDate) {
            isSameDay = timeDay.count(currDate, nextDate) <= 0;
        }
        return !isSameDay;
    };

    // NB: skip first comment, as it is shown in the chat body
    const renderAttachments = (comment: IMessagingComment): React.ReactElement | React.ReactElement[] | undefined => {
        const commentDocIds = comment.documents || [];
        const commentDocs = _.filter(documents, document => _.includes(commentDocIds, document.id));
        
        // Show loading indicator when documents are being fetched
        if (commentDocIds.length > 0 && commentDocs.length === 0) {
            return <S.LoadingText>Loading documents...</S.LoadingText>;
        }
        
        return _.times(
            _.size(commentDocs),
            (i: number): React.ReactElement => {
                const doc = commentDocs[i];
                const onDownload = async (): Promise<void> => {
                    if (doc.id) {
                        await (context.onDocumentDownload && context.onDocumentDownload(doc.id));
                    }
                };

                const onActualDownload = async (): Promise<string | void> => {
                    if (doc && doc.id && context.onDocumentActualDownload) {
                        return await context.onDocumentActualDownload(doc.id);
                    }
                };

                const onAssign = async (): Promise<void> => {
                    await (context.onDocumentAssign && context.onDocumentAssign(doc));
                };

                const onEdit = async (): Promise<void> => {
                    await (context.onDocumentEdit && context.onDocumentEdit(doc));
                };

                const isSupportApp = context.account?.supportGrant === true;
                const canAssign = context.message?.links?.claims?.length && context.message?.links?.claims?.length > 0 && !doc.claimId && !!context.onDocumentAssign;

                return (
                    <S.HistoryEntryAttachment key={`attachment-${i}`}>
                        <AttachmentButton
                            key={`attachment-${i}`}
                            filename={doc.filename as string}
                            filesize={doc.filesize}
                            thumbnail={doc.thumbnail}
                            documentId={doc.id}
                            onDownload={onDownload}
                            onActualDownload={onActualDownload}
                            onAssign={canAssign ? onAssign : undefined}
                            onEdit={context.onDocumentEdit ? onEdit : undefined}
                            isSupportApp={isSupportApp}
                        />
                    </S.HistoryEntryAttachment>
                );
            },
        );
    };

    const renderHeader = (date: Date, i: number): React.ReactElement => (
        <S.HistoryHeader key={`header-${date}${i}`}>
            <S.HistoryHeaderLine />
            <S.HistoryHeaderLabel>{mdyFormat(date)}</S.HistoryHeaderLabel>
            <S.HistoryHeaderLine />
        </S.HistoryHeader>
    );

    const renderComment = (comment: IMessagingComment, i: number): React.ReactElement => {
        const hasNote = _.get(comment, 'note', false);
        const isHtml = isHtmlContent(comment.body);
        
        return (
            <Grow in={true} key={comment.id}>
                <S.HistoryEntry note={hasNote}>
                    <S.HistoryEntryTopic>
                        <S.HistoryAuthorIndicator unread={false} author={comment.author} />
                        <S.HistoryEntryName>{authorNickname(comment)}</S.HistoryEntryName>
                        <S.HistoryEntryTime>{timeFormat(parseDate(comment.created))}</S.HistoryEntryTime>
                        <S.HistoryNoteSpacer />
                        {hasNote && <NoteIndicator comment={comment} />}
                    </S.HistoryEntryTopic>
                    <S.HistoryEntryMessage>
                        {isHtml ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
                        ) : (
                            <Markdown>{normalizeMarkdown(comment.body || '')}</Markdown>
                        )}
                    </S.HistoryEntryMessage>
                    {renderAttachments(comment)}
                </S.HistoryEntry>
            </Grow>
        );
    };

    const renderComments = (): React.ReactElement[] => {
        const result: React.ReactElement[] = [];
        let lastDay: Date | undefined;
        const historyComments = _.tail<IMessagingComment>(comments as IMessagingComment[]);
        _.forEach(historyComments, (comment: IMessagingComment | undefined, i: number): void => {
            if (comment) {
                if (hideNote && comment.note) {
                    return;
                }
                const date = parseDate(comment.created);
                if (isNewDay(lastDay, date)) {
                    result.push(renderHeader(date, i));
                }
                result.push(renderComment(comment, i));
                lastDay = date;
            }
        });
        return result;
    };

    const historyComments = _.tail<IMessagingComment>(comments as IMessagingComment[]);
    const commentsCount = hideNote 
        ? historyComments.filter(c => !c.note).length 
        : historyComments.length;

    return (
        <S.History>
            <S.CommentsHeader>
                <S.CommentsHeaderTitle>
                    <S.CommentsIcon>
                        <Icon path={mdiMessageTextOutline} size={0.6} color="#3b82f6" />
                    </S.CommentsIcon>
                    <S.CommentsLabel>Comments</S.CommentsLabel>
                    <S.CommentsBadge>{commentsCount}</S.CommentsBadge>
                </S.CommentsHeaderTitle>
            </S.CommentsHeader>
            <S.HistoryBody>{renderComments()}</S.HistoryBody>
        </S.History>
    );
};
